'use client';

import { useLocale } from 'next-intl';
import { api, useQuery } from '@/lib/convex';
import { REALTIME_VOICE_MIN_START_CREDITS } from '@/convex/config/pricing_constants';
import { formatInsufficientCreditsMessage } from '@/lib/credit-costs';
import {
  isActionBlockedForCredits,
  type CreditAvailability,
} from '@/lib/credit-gating';
import { getRealtimeVoiceCallCopy } from '@/i18n/realtime-voice-call-copy';

export type VoiceCallCreditGate = {
  cost: number;
  creditsInfo: CreditAvailability;
  /** True only once the balance is known to be below the start minimum. */
  isBlocked: boolean;
  message: string;
};

/**
 * Pre-start credit check for live voice calls. Mirrors the server minimum in
 * `assertRealtimeVoiceSessionAffordable`, which stays the backstop.
 */
export function useVoiceCallCreditGate(
  orgSlug: string | undefined
): VoiceCallCreditGate {
  const locale = useLocale();
  const { data: creditsInfo } = useQuery(
    api.credits.queries.getAvailableCreditsBySlug,
    orgSlug ? { orgSlug } : 'skip'
  );
  return {
    cost: REALTIME_VOICE_MIN_START_CREDITS,
    creditsInfo,
    isBlocked: isActionBlockedForCredits({
      creditsInfo,
      cost: REALTIME_VOICE_MIN_START_CREDITS,
    }),
    message: formatInsufficientCreditsMessage(
      getRealtimeVoiceCallCopy(locale).insufficientCreditsError,
      REALTIME_VOICE_MIN_START_CREDITS
    ),
  };
}
